import { ENVIRONMENT } from "src/js/util/environment";
import { useEffect, useState } from "react";
import { Nullable } from "react-migration/domains/property/typings";
import {
  PlanningApplication,
  PlanningApplicationSchema,
} from "react-migration/domains/planning/types";
import { ZodError } from "zod";
import { SelectionGeometry } from "src/js/stores/map/store";

type RawApplication = Partial<PlanningApplication> & { boundary: string };

function parseApplications(data: RawApplication[] = []) {
  const applications: PlanningApplication[] = [];
  const invalidApplications: {
    input: Partial<PlanningApplication>;
    error: ZodError<PlanningApplication>;
  }[] = [];

  data
    .map((application) => ({
      input: application,
      parsedApplication: PlanningApplicationSchema.safeParse(application),
    }))
    .forEach(({ input, parsedApplication }) => {
      if (parsedApplication.success) {
        applications.push(parsedApplication.data);
      } else {
        invalidApplications.push({ input, error: parsedApplication.error });
      }
    });

  return [applications, invalidApplications] as const;
}

export function usePlanningApplicationsByGeometry(geometry: Nullable<SelectionGeometry>) {
  const [data, setData] = useState<PlanningApplication[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    async function fetchPlanningApplications() {
      try {
        setLoading(true);
        const response = await fetch(
          `${ENVIRONMENT.PLANNING_SERVICE_URL}/planning-applications/within/v2`,
          {
            method: "POST",
            body: JSON.stringify({ geometry }),
            headers: {
              "Content-Type": "application/json",
            },
            mode: "cors",
            signal: controller.signal,
          }
        );

        const { applications } = await response.json();

        const [parsedApplications, invalidApplications] = parseApplications(applications);

        invalidApplications.forEach(({ input, error }) =>
          console.error(`Invalid Planning Application response: ${input?.id}`, { error })
        );
        setData(parsedApplications);
      } catch (error) {
        console.error(error);
        setData([]);
      } finally {
        setLoading(false);
      }
    }

    geometry ? fetchPlanningApplications() : setData([]);

    return () => controller.abort();
  }, [geometry]);

  return { data, loading };
}
